/* eslint-disable camelcase */
import queryString from 'query-string';

import { getJobIdFromUrl, getStageIdFromUrl } from 'utils/urlUtils';

import { QueryParams } from './types';

/**
 * Builds the queryParams forwarded to ApplicantTableV2, and thus the
 * /api_self_serve/v2/applicants request, for every surface that renders the
 * applicants table: the cross-opening master view, a single opening, and the
 * refetches that follow a filter apply or a bulk action.
 *
 * The URL is the source of truth for that request. Every producer reads it
 * through this module so a filter cannot reach the toolbar without reaching
 * the request.
 *
 * Multi-value filter params arrive from the URL as arrays (query-string
 * default / repeated-key format, e.g. location_ids=a&location_ids=b) or as a
 * scalar when a single value is selected. Each is normalized to an array so
 * the request serializes as `name[]=…`, which is what the monolith's strong
 * params (`permit(location_ids: [])`) require.
 */

type ParsedSearch = ReturnType<typeof queryString.parse>;
type ParsedValue = ParsedSearch[string];

/**
 * How a param's raw URL value becomes its request value.
 *
 * `scalar` forwards the value as-is. `array` normalizes one or many values to
 * an array and omits the param when absent. `booleanTrue` is a toggle written
 * to the URL only when on, so anything but the string "true" omits it rather
 * than sending `false`.
 */
type ParamKind = 'scalar' | 'array' | 'booleanTrue';

interface ParamSpec {
  /** Key as written in the URL. */
  urlKey: string;
  /** Key the monolith expects, when it differs from the URL key. */
  requestKey?: string;
  kind: ParamKind;
}

/** Page and sort. Always forwarded, never a filter. */
const PAGINATION_PARAMS: ParamSpec[] = [
  { urlKey: 'page', kind: 'scalar' },
  { urlKey: 'per_page', kind: 'scalar' },
  { urlKey: 'sort_column', kind: 'scalar' },
  { urlKey: 'sort_direction', kind: 'scalar' },
];

/**
 * The toolbar filters. A select-all bulk action re-runs the list query on the
 * monolith, so these ride on the bulk action request as well; see
 * buildTableFilterParams.
 */
const TABLE_FILTER_PARAMS: ParamSpec[] = [
  { urlKey: 'assigned_to_logged_in_user', kind: 'booleanTrue' },
  { urlKey: 'workflow_ids', kind: 'array' },
  { urlKey: 'stage_external_ids', kind: 'array' },
  { urlKey: 'location_group_ids', kind: 'array' },
  { urlKey: 'location_ids', kind: 'array' },
  { urlKey: 'brand_ids', kind: 'array' },
];

/**
 * Every param that narrows the result set, declared once. A new filter is one
 * entry here; leaving it out is the defect this module exists to prevent.
 *
 * `filter_set_id` and `query` are absent on purpose: both need a rule this
 * table cannot express, and both are applied by hand in the builder below.
 */
const FILTER_PARAMS: ParamSpec[] = [
  { urlKey: 'stage_conditions_filter_set_id', kind: 'scalar' },
  { urlKey: 'duplicate_applicant_id', kind: 'scalar' },
  ...TABLE_FILTER_PARAMS,
  { urlKey: 'applicantIds', requestKey: 'applicant_ids', kind: 'array' },
];

const toArray = (value: ParsedValue) =>
  Array.isArray(value) ? value : [value];

/**
 * Applies one spec against the parsed URL. Returns a spreadable object so a
 * param with nothing to send contributes no key at all, rather than an
 * explicit `undefined` that the serializer would have to strip.
 */
const coerce = (
  spec: ParamSpec,
  parsed: ParsedSearch,
): Record<string, unknown> => {
  const value = parsed[spec.urlKey];
  const key = spec.requestKey ?? spec.urlKey;

  switch (spec.kind) {
    case 'booleanTrue':
      return value === 'true' ? { [key]: true } : {};
    case 'array':
      return value ? { [key]: toArray(value) } : {};
    default:
      return value === undefined || value === null ? {} : { [key]: value };
  }
};

const fromSpecs = (specs: ParamSpec[], parsed: ParsedSearch) =>
  specs.reduce(
    (acc, spec) => Object.assign(acc, coerce(spec, parsed)),
    {} as Record<string, unknown>,
  );

interface BuildOptions {
  /**
   * Search text held in Redux while the user types. Falls back to the URL's
   * own `query` param for callers that have no store to read.
   */
  query?: string;
  /**
   * Opening scope, for a caller that already holds it. The route match below
   * is the fallback, and it is the weaker source: the opening route is
   * declared without `exact`, so a deeper URL under it still renders the
   * table while the match returns nothing. A container with route params
   * passes them rather than having the path re-parsed underneath it.
   */
  stageId?: string;
  funnelId?: string;
}

/**
 * Pure: reads the URL and returns the request params. Seeding missing page and
 * per_page defaults into the URL belongs to the container that owns the view.
 */
export function buildApplicantsQueryParams(
  location: Location,
  { query, stageId, funnelId }: BuildOptions = {},
): QueryParams {
  const parsed = queryString.parse(location.search);

  return {
    ...fromSpecs(PAGINATION_PARAMS, parsed),
    ...fromSpecs(FILTER_PARAMS, parsed),
    // A temporary filter is a saved filter the user has not named yet, so the
    // monolith reads both under the one param.
    filter_set_id: (parsed.filter_set_id || parsed.temp_filter_id) as
      | string
      | undefined,
    query: query ?? (parsed.query as string | undefined),
    // Both stay undefined on the master view, which is what lets one builder
    // serve the cross-opening table and a single opening alike.
    stage_id: stageId ?? getStageIdFromUrl({ location }),
    funnel_id: funnelId ?? getJobIdFromUrl({ location }),
  } as QueryParams;
}

/**
 * The toolbar filters for a bulk action request. A bulk action that omits one
 * reaches every applicant the user can access, not the rows on screen.
 */
export function buildTableFilterParams(
  location: Pick<Location, 'search'>,
): Record<string, unknown> {
  return fromSpecs(TABLE_FILTER_PARAMS, queryString.parse(location.search));
}
